import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getClearance } from "../lib/catalogueApi";
import { ProductCard } from "./ProductCard";
import { ArrowRight, BadgeCheck, ClipboardCheck, CreditCard, Info } from "lucide-react";
import { money } from "./product/BuyBox";

// T48: Ex-Demo & Clearance building blocks (calm, premium B2B; no discount-store styling).
export const HOW = [
  [ClipboardCheck, "Order with no payment now", "Place your order as usual."],
  [BadgeCheck, "We confirm the details", "Condition, inclusions, warranty and photos where available."],
  [CreditCard, "Pay only if you're happy", "You decide once you've reviewed the details."],
];

export function HowItWorks({ testid = "clearance-how" }) {
  return (
    <ol data-testid={testid} className="grid gap-3 sm:grid-cols-3" aria-label="How it works">
      {HOW.map(([I, t, d], i) => (
        <li key={t} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4">
          <I size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-[var(--brand-link)]" />
          <div><div className="text-sm font-semibold text-slate-900">{i + 1}. {t}</div><div className="text-[13px] text-slate-600">{d}</div></div>
        </li>))}
    </ol>
  );
}

export function SavingLine({ p, testid }) {
  if (!p.saving_inc_gst || Number(p.saving_inc_gst) <= 0) return null;
  return <div data-testid={testid || "clearance-saving"} className="text-[12px] font-semibold leading-4 text-emerald-700">Save {money(p.saving_inc_gst)} vs new</div>;
}

export function ClearanceAltLink({ alt }) {
  if (!alt) return null;
  return (
    <Link to={`/p/${alt.slug}`} data-testid="pdp-clearance-alt" className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-[var(--brand-primary)] hover:bg-slate-100">
      Also available as Ex-Demo &amp; Clearance from {money(alt.from_inc_gst)} <ArrowRight size={14} aria-hidden="true" />
    </Link>
  );
}

export function ClearancePdpBlock({ p }) {
  const c = p.clearance;
  if (!c) return null;
  return (
    <section data-testid="pdp-clearance-block" className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 md:p-6" aria-label="Ex-Demo & Clearance">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="font-semibold text-slate-900">Ex-Demo &amp; Clearance</span>
        {Number(p.price_inc_gst) > 0 && <span data-testid="pdp-clearance-price">Price: <b>{money(p.price_inc_gst)}</b> inc GST</span>}
        <span data-testid="pdp-clearance-stock">Availability: <b>{c.clearance_reserved ? "Currently unavailable" : c.stock_qty > 0 ? `${c.stock_qty} available` : "Out of stock"}</b></span>
        {c.new_unit && <span data-testid="pdp-clearance-compare">New unit: <Link className="font-semibold text-[var(--brand-link)] hover:underline" to={`/p/${c.new_unit.slug}`} data-testid="pdp-clearance-new-unit">{money(c.new_unit.price_inc_gst)}</Link>
          {c.saving_inc_gst && <b className="ml-1 text-emerald-700">· Save {money(c.saving_inc_gst)}</b>}</span>}
      </div>
      <div className="mt-2 flex items-start gap-2 text-[13px] text-slate-600" data-testid="pdp-clearance-no-payment"><Info size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
        No payment now: we confirm the item's condition, inclusions and warranty before you pay.</div>
      <div className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-700 [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3:first-child]:mt-0 [&_p]:mt-1" data-testid="pdp-clearance-notice" dangerouslySetInnerHTML={{ __html: c.notice_html }} />
      {c.staff_note && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="pdp-clearance-staff-note">{c.staff_note}</p>}
    </section>
  );
}

export function ClearanceHome() {
  const [items, setItems] = useState(null);
  useEffect(() => { getClearance({ size: 8 }).then((d) => setItems((d.items || []).filter((i) => !i.purchase_state || i.purchase_state === "purchasable").slice(0, 4)))  /* T66: homepage = purchasable only */.catch(() => setItems([])); }, []);
  if (!items) return <div aria-hidden="true" data-testid="home-clearance-placeholder" className="min-h-[950px] sm:min-h-[1140px] md:min-h-[520px] lg:min-h-[565px] xl:min-h-[600px]" />;  // T57: reserve the loaded height (no shift)
  if (!items.length) return null;
  return (
    <section className="py-6" data-testid="home-clearance" aria-labelledby="home-clearance-title">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div><h2 id="home-clearance-title" className="rs-h2">Ex-Demo &amp; Clearance</h2>
          <p className="text-sm text-slate-600">Quality business technology at better prices. Condition confirmed before you pay.</p></div>
        <Link to="/ex-demo-clearance" data-testid="home-clearance-view-all" className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--brand-link)] hover:underline">View all <ArrowRight size={14} aria-hidden="true" /></Link>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{items.map((p) => <ProductCard key={p.id} p={p} buy />)}</div>
    </section>
  );
}
